import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { register, login, clearError } from '../store/slices/authSlice';
import api from '../api';

function SuperAdminRegister() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'superadmin') {
      navigate('/super-admin/dashboard');
    }
  }, [isAuthenticated, user, navigate]);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validateForm()) {
      return;
    }

    dispatch(clearError());

    try {
      // Register the user
      const registerResult = await dispatch(register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: 'superadmin'
      }));

      if (register.fulfilled.match(registerResult)) {
        // Auto-login after registration
        const loginResult = await dispatch(login({
          email: formData.email,
          password: formData.password
        }));

        if (login.fulfilled.match(loginResult)) {
          navigate('/super-admin/dashboard');
        }
      } else {
        setServerError(registerResult.payload?.message || 'Registration failed');
      }
    } catch (err) {
      setServerError(err.response?.data?.message || 'Registration failed. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="login-page" style={styles.page}>
      <div className="login-card" style={styles.card}>
        <div style={styles.logoSection}>
          <div style={styles.logo}>⚡</div>
          <h1 style={styles.title}>Create Super Admin</h1>
          <p style={styles.subtitle}>Register a new platform administrator</p>
        </div>

        {serverError && <div style={styles.error}>{serverError}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Full Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              style={validationErrors.name ? { ...styles.input, borderColor: '#dc2626' } : styles.input}
            />
            {validationErrors.name && <span style={styles.fieldError}>{validationErrors.name}</span>}
          </div>

          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Email Address *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="superadmin@example.com"
              style={validationErrors.email ? { ...styles.input, borderColor: '#dc2626' } : styles.input}
            />
            {validationErrors.email && <span style={styles.fieldError}>{validationErrors.email}</span>}
          </div>

          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Password *</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Minimum 8 characters"
              style={validationErrors.password ? { ...styles.input, borderColor: '#dc2626' } : styles.input}
            />
            {validationErrors.password && <span style={styles.fieldError}>{validationErrors.password}</span>}
          </div>

          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Confirm Password *</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              style={validationErrors.confirmPassword ? { ...styles.input, borderColor: '#dc2626' } : styles.input}
            />
            {validationErrors.confirmPassword && <span style={styles.fieldError}>{validationErrors.confirmPassword}</span>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={styles.button}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div style={styles.footer}>
          <span style={styles.footerText}>Already have an account?</span>
          <Link to="/super-admin/login" style={styles.link}>Sign In</Link>
        </div>

        <div style={styles.backLink}>
          <a href="/login" style={styles.link}>← Back to Main Login</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logo: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#6b7280',
    fontSize: '14px',
    margin: 0,
  },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  fieldError: {
    color: '#dc2626',
    fontSize: '12px',
  },
  button: {
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'opacity 0.2s',
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center',
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
  },
  footerText: {
    color: '#6b7280',
    fontSize: '14px',
  },
  link: {
    color: '#667eea',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '600',
  },
  backLink: {
    marginTop: '16px',
    textAlign: 'center',
  },
};

export default SuperAdminRegister;